/**
 * offer-comparison.ts - the KI-Offertvergleich + Offer-Score contract (P2 Placement Slice 11,
 * Ausschreibungs-Screen Tab 4b, Prozessschritte 11 + 12; Detailkonzept Ausschreibung V3.0
 * Ch 6.5 / 7.2 / 8.6 / 9 / 10 / 11, P2 Section 7 steps 11 + 12, Section 8 E4, Section 10.1, Ue5/Ue6/Ue7).
 *
 * The SINGLE SOURCE OF TRUTH for:
 *   - the comparison report review lifecycle (entwurf -> freigegeben, ersetzt) + the KI pipeline state,
 *   - the Ch-7.2 Ampel vocabulary + the Ue7 Offer-Score dimensions and default weights,
 *   - the PURE comparison (berechneOffertVergleich -> matrix + sub-scores + total + E4 + ranking)
 *     shared so api + web + tests agree on every number,
 *   - the staleness computation + the fingerprint over the Soll answers,
 *   - the wire shapes (the report / response / body DTOs).
 *
 * Pure module - no I/O, no formatters, no Date-TZ parse. The only non-trivial primitive is a tiny
 * pure sha256 (there is no shared sha256 helper), used by sollAntwortenFingerprint.
 *
 * [Source: docs/product/p2-slices/slice-11-offer-compare-design.md Section 3]
 * [Source: packages/shared/src/offer.ts] (triad + wire idioms)
 */

import type { PlacementStatus } from './placement';
import type { OfferDeckungStatus, OfferNormalizedV1 } from './offer';
import type { SollPflichtStatus } from './police-risiko';
import type { Step2AnswerStatus } from './placement-snapshot';

// -- Vocabularies (triad idiom) ------------------------------------------------

/** The review lifecycle of one comparison run. */
export const COMPARISON_REPORT_STATUS = {
  ENTWURF: 'entwurf',
  FREIGEGEBEN: 'freigegeben',
  ERSETZT: 'ersetzt',
} as const;
export type ComparisonReportStatus =
  (typeof COMPARISON_REPORT_STATUS)[keyof typeof COMPARISON_REPORT_STATUS];
export const COMPARISON_REPORT_STATUS_VALUES: readonly ComparisonReportStatus[] = [
  COMPARISON_REPORT_STATUS.ENTWURF,
  COMPARISON_REPORT_STATUS.FREIGEGEBEN,
  COMPARISON_REPORT_STATUS.ERSETZT,
];
export const COMPARISON_REPORT_STATUS_LABELS: Record<ComparisonReportStatus, string> = {
  [COMPARISON_REPORT_STATUS.ENTWURF]: 'Entwurf - Freigabe ausstehend',
  [COMPARISON_REPORT_STATUS.FREIGEGEBEN]: 'Freigegeben',
  [COMPARISON_REPORT_STATUS.ERSETZT]: 'Ersetzt',
};

/** The KI-generation pipeline state of one run. */
export const COMPARISON_GENERATION_STATUS = {
  LAUFEND: 'laufend',
  FERTIG: 'fertig',
  FEHLGESCHLAGEN: 'fehlgeschlagen',
} as const;
export type ComparisonGenerationStatus =
  (typeof COMPARISON_GENERATION_STATUS)[keyof typeof COMPARISON_GENERATION_STATUS];
export const COMPARISON_GENERATION_STATUS_VALUES: readonly ComparisonGenerationStatus[] = [
  COMPARISON_GENERATION_STATUS.LAUFEND,
  COMPARISON_GENERATION_STATUS.FERTIG,
  COMPARISON_GENERATION_STATUS.FEHLGESCHLAGEN,
];
export const COMPARISON_GENERATION_STATUS_LABELS: Record<ComparisonGenerationStatus, string> = {
  [COMPARISON_GENERATION_STATUS.LAUFEND]: 'KI-Bewertung wird erstellt',
  [COMPARISON_GENERATION_STATUS.FERTIG]: 'KI-Bewertung liegt vor',
  [COMPARISON_GENERATION_STATUS.FEHLGESCHLAGEN]: 'KI-Bewertung fehlgeschlagen',
};

/** The Ch-7.2 Ampel of one matrix cell (the offer row never carries `neutral`). */
export const VERGLEICH_AMPEL = {
  GRUEN: 'gruen',
  GELB: 'gelb',
  ROT: 'rot',
  GRAU: 'grau',
  NEUTRAL: 'neutral',
} as const;
export type VergleichAmpel = (typeof VERGLEICH_AMPEL)[keyof typeof VERGLEICH_AMPEL];
export const VERGLEICH_AMPEL_VALUES: readonly VergleichAmpel[] = [
  VERGLEICH_AMPEL.GRUEN,
  VERGLEICH_AMPEL.GELB,
  VERGLEICH_AMPEL.ROT,
  VERGLEICH_AMPEL.GRAU,
  VERGLEICH_AMPEL.NEUTRAL,
];
export const VERGLEICH_AMPEL_LABELS: Record<VergleichAmpel, string> = {
  [VERGLEICH_AMPEL.GRUEN]: 'erfuellt',
  [VERGLEICH_AMPEL.GELB]: 'teilweise erfuellt',
  [VERGLEICH_AMPEL.ROT]: 'nicht erfuellt',
  [VERGLEICH_AMPEL.GRAU]: 'nicht erkannt',
  [VERGLEICH_AMPEL.NEUTRAL]: 'informativ',
};

/** The three row kinds of the comparison matrix. */
export const VERGLEICH_ZEILE_ART = {
  KENNZAHL: 'kennzahl',
  SOLL_REGEL: 'soll_regel',
  ZUSATZDECKUNG: 'zusatzdeckung',
} as const;
export type VergleichZeileArt = (typeof VERGLEICH_ZEILE_ART)[keyof typeof VERGLEICH_ZEILE_ART];
export const VERGLEICH_ZEILE_ART_VALUES: readonly VergleichZeileArt[] = [
  VERGLEICH_ZEILE_ART.KENNZAHL,
  VERGLEICH_ZEILE_ART.SOLL_REGEL,
  VERGLEICH_ZEILE_ART.ZUSATZDECKUNG,
];
export const VERGLEICH_ZEILE_ART_LABELS: Record<VergleichZeileArt, string> = {
  [VERGLEICH_ZEILE_ART.KENNZAHL]: 'Kennzahl',
  [VERGLEICH_ZEILE_ART.SOLL_REGEL]: 'Soll-Vorgabe (Schritt 4)',
  [VERGLEICH_ZEILE_ART.ZUSATZDECKUNG]: 'Zusatzdeckung',
};

/** The seven Ue7 Offer-Score dimensions (order = the Ue7 rows verbatim). */
export const OFFER_SCORE_DIMENSION = {
  DECKUNG: 'deckung',
  PREIS: 'preis',
  SELBSTBEHALT: 'selbstbehalt',
  AUSSCHLUESSE: 'ausschluesse',
  VERSICHERERQUALITAET: 'versichererqualitaet',
  SERVICE: 'service',
  INDIVIDUELL: 'individuell',
} as const;
export type OfferScoreDimension =
  (typeof OFFER_SCORE_DIMENSION)[keyof typeof OFFER_SCORE_DIMENSION];
export const OFFER_SCORE_DIMENSION_VALUES: readonly OfferScoreDimension[] = [
  OFFER_SCORE_DIMENSION.DECKUNG,
  OFFER_SCORE_DIMENSION.PREIS,
  OFFER_SCORE_DIMENSION.SELBSTBEHALT,
  OFFER_SCORE_DIMENSION.AUSSCHLUESSE,
  OFFER_SCORE_DIMENSION.VERSICHERERQUALITAET,
  OFFER_SCORE_DIMENSION.SERVICE,
  OFFER_SCORE_DIMENSION.INDIVIDUELL,
];
export const OFFER_SCORE_DIMENSION_LABELS: Record<OfferScoreDimension, string> = {
  [OFFER_SCORE_DIMENSION.DECKUNG]: 'Deckung',
  [OFFER_SCORE_DIMENSION.PREIS]: 'Preis',
  [OFFER_SCORE_DIMENSION.SELBSTBEHALT]: 'Selbstbehalt',
  [OFFER_SCORE_DIMENSION.AUSSCHLUESSE]: 'Ausschluesse',
  [OFFER_SCORE_DIMENSION.VERSICHERERQUALITAET]: 'Versichererqualitaet',
  [OFFER_SCORE_DIMENSION.SERVICE]: 'Service/Schadenabwicklung',
  [OFFER_SCORE_DIMENSION.INDIVIDUELL]: 'Individuelle Kundenanforderungen',
};

/** Where the weights a run used came from. */
export const GEWICHTE_QUELLE = {
  TENANT: 'tenant',
  REPORT: 'report',
} as const;
export type GewichteQuelle = (typeof GEWICHTE_QUELLE)[keyof typeof GEWICHTE_QUELLE];
export const GEWICHTE_QUELLE_VALUES: readonly GewichteQuelle[] = [
  GEWICHTE_QUELLE.TENANT,
  GEWICHTE_QUELLE.REPORT,
];
export const GEWICHTE_QUELLE_LABELS: Record<GewichteQuelle, string> = {
  [GEWICHTE_QUELLE.TENANT]: 'Mandanten-Standard',
  [GEWICHTE_QUELLE.REPORT]: 'Fuer diesen Vergleich angepasst',
};

/** Why a report is no longer current. */
export const VERALTET_GRUND = {
  NEUE_OFFERTVERSION: 'neue_offertversion',
  OFFER_ENTFERNT: 'offer_entfernt',
  SOLL_GEAENDERT: 'soll_geaendert',
} as const;
export type VeraltetGrund = (typeof VERALTET_GRUND)[keyof typeof VERALTET_GRUND];
export const VERALTET_GRUND_VALUES: readonly VeraltetGrund[] = [
  VERALTET_GRUND.NEUE_OFFERTVERSION,
  VERALTET_GRUND.OFFER_ENTFERNT,
  VERALTET_GRUND.SOLL_GEAENDERT,
];
export const VERALTET_GRUND_LABELS: Record<VeraltetGrund, string> = {
  [VERALTET_GRUND.NEUE_OFFERTVERSION]: 'Neue Offertversion eingegangen',
  [VERALTET_GRUND.OFFER_ENTFERNT]: 'Eine verglichene Offerte ist nicht mehr aktuell',
  [VERALTET_GRUND.SOLL_GEAENDERT]: 'Soll-Vorgaben wurden geaendert',
};

// -- Status machine ------------------------------------------------------------

export const COMPARISON_REPORT_STATUS_TRANSITIONS: Record<
  ComparisonReportStatus,
  readonly ComparisonReportStatus[]
> = {
  entwurf: ['freigegeben', 'ersetzt'],
  freigegeben: ['ersetzt'],
  ersetzt: [],
};

/** Is `to` a legal next comparison-report status from `from`? */
export function canTransitionComparisonReport(
  from: ComparisonReportStatus,
  to: ComparisonReportStatus,
): boolean {
  return COMPARISON_REPORT_STATUS_TRANSITIONS[from].includes(to);
}

// -- Gates / constants ---------------------------------------------------------

/** Placement statuses in which a compare run may be triggered. */
export const VERGLEICH_ERLAUBTE_STATUS: readonly PlacementStatus[] = [
  'ausgeschrieben',
  'verglichen',
  'empfohlen',
];
/** Placement statuses in which a report may be edited / released. */
export const VERGLEICH_BEARBEITBARE_STATUS: readonly PlacementStatus[] = ['verglichen', 'empfohlen'];
/** Minimum normalised offers for a comparison (Ch 8.6). */
export const OFFER_VERGLEICH_MIN_OFFERTEN = 2;
/** Max KI-generation attempts before the terminal failure task. */
export const OFFER_COMPARISON_MAX_ATTEMPTS = 3;
/**
 * A report stuck in generation_status='laufend' longer than this (seconds, measured against the row's
 * updated_at) is treated as a dead KI job: «Vergleich ausfuehren» / «KI-Bewertung erneut erzeugen» may
 * then supersede / retry it instead of refusing with generierung_laeuft, so a lost post-commit enqueue
 * or a dead-lettered job can never deadlock the placement (review M2).
 */
export const OFFER_COMPARISON_LAUFEND_STALE_SECONDS = 600;
/** Minimum Begruendung length before Freigabe. */
export const OFFER_EMPFEHLUNG_BEGRUENDUNG_MIN = 20;
/** Premium Ampel: cheapest + this many % still gelb, above = rot. */
export const OFFER_PREIS_GELB_BIS_PROZENT = 10;
/** Coverage gap at/above this % = rot, below = gelb. */
export const OFFER_AMPEL_GAP_ROT_AB_PROZENT = 20;
/** Selbstbehalt Ampel: up to Soll x this factor still gelb. */
export const OFFER_SB_GELB_BIS_FAKTOR = 1.2;
/** Points deducted per Ausschluss from the Ausschluesse sub-score. */
export const OFFER_SCORE_AUSSCHLUSS_ABZUG = 15;
/** Flat E4 penalty on score_total. */
export const OFFER_SCORE_E4_ABWERTUNG_PUNKTE = 10;
/** Neutral default for the two broker-only sub-scores. */
export const OFFER_SCORE_NEUTRAL = 50;
/** Cell score per Ampel for the deckung/selbstbehalt sub-scores (neutral = excluded). */
export const OFFER_SCORE_AMPEL_PUNKTE: Record<VergleichAmpel, number | null> = {
  gruen: 100,
  gelb: 60,
  rot: 0,
  grau: 50,
  neutral: null,
};
/** Row weight for the deckung sub-score (by Pflicht-Status; Zusatzdeckungen = 'zusatz'). */
export const OFFER_SCORE_REGEL_GEWICHT: Record<SollPflichtStatus | 'zusatz', number> = {
  pflicht: 3,
  bedingt_pflicht: 2,
  empfehlung: 2,
  optional: 1,
  zusatz: 1,
};
/** Versichererqualitaet base by eignung. */
export const OFFER_SCORE_EIGNUNG_BASIS = {
  geeignet: 70,
  unbekannt: 40,
  ausgeschlossen: 10,
} as const;
/** Max Versichererqualitaet bonus from active policies. */
export const OFFER_SCORE_EIGNUNG_BONUS_MAX = 30;

export type OfferScoreGewichte = Record<OfferScoreDimension, number>;

/** The Ue7 default weights (sum = 100; a spec asserts the sum). */
export const OFFER_SCORE_GEWICHTE_DEFAULT: OfferScoreGewichte = {
  deckung: 35,
  preis: 25,
  selbstbehalt: 10,
  ausschluesse: 15,
  versichererqualitaet: 5,
  service: 5,
  individuell: 5,
};
/** The catalog_version stamp for the default weights. */
export const OFFER_SCORE_GEWICHTE_VERSION = 'u7-v1';

/** The OOXML .docx MIME (re-declared here for web; api keeps its own copy). */
export const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Sum of the seven weights. */
export function gewichteSumme(g: OfferScoreGewichte): number {
  return OFFER_SCORE_DIMENSION_VALUES.reduce((acc, dim) => acc + (g[dim] ?? 0), 0);
}

/** Seven integers in [0, 100] summing to exactly 100. */
export function sindGewichteGueltig(g: OfferScoreGewichte): boolean {
  for (const dim of OFFER_SCORE_DIMENSION_VALUES) {
    const v = g[dim];
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > 100) return false;
  }
  return gewichteSumme(g) === 100;
}

// -- Comparison model (pure) ---------------------------------------------------

export interface VergleichSollRegel {
  rule_key: string;
  label: string;
  pflicht_status: SollPflichtStatus;
  effective_target_value: number | null;
  selbstbehalt_override: number | null;
  status: Step2AnswerStatus;
}

export interface VergleichInputOffer {
  offer_document_id: string;
  submission_id: string;
  submission_number: string;
  carrier_uid: string;
  carrier_name: string;
  logo_url: string | null;
  version_no: number;
  normalized: OfferNormalizedV1;
  eignung: 'geeignet' | 'unbekannt' | 'ausgeschlossen' | null;
  aktive_policen: number | null;
  broker: { service_subscore: number | null; individuell_subscore: number | null };
}

export interface VergleichInput {
  offerten: VergleichInputOffer[];
  soll: VergleichSollRegel[];
  gewichte: OfferScoreGewichte;
}

export interface VergleichZelle {
  ist_text: string;
  wert: number | null;
  status: OfferDeckungStatus | null;
  gap_prozent: number | null;
  ampel: VergleichAmpel;
  hinweis: string | null;
}

/** One matrix row; `zellen` keyed by offer_document_id. */
export interface VergleichZeile {
  key: string;
  art: VergleichZeileArt;
  label: string;
  soll_text: string;
  mindestanforderung: boolean;
  rule_key: string | null;
  zellen: Record<string, VergleichZelle>;
}

export interface VergleichSubscore {
  wert: number;
  quelle: 'berechnet' | 'broker' | 'default';
  hinweis: string | null;
}
export type VergleichSubscores = Record<OfferScoreDimension, VergleichSubscore>;

export interface VergleichKennzahlen {
  praemie_jahr: number | null;
  versicherungssumme: number | null;
  selbstbehalt: number | null;
  vertragsdauer_monate: number | null;
  waehrung: string;
}

export interface OfferScoreErgebnis {
  offer_document_id: string;
  rang: number;
  score_total: number;
  score_vor_abwertung: number;
  subscores: VergleichSubscores;
  ampel: Exclude<VergleichAmpel, 'neutral'>;
  e4_flag: boolean;
  e4_rule_keys: string[];
  kennzahlen: VergleichKennzahlen;
  ausschluesse: string[];
  /** This offer's cells in row order (what gets persisted as soll_ist_json). */
  soll_ist: Array<{ zeile_key: string } & VergleichZelle>;
}

export interface VergleichErgebnis {
  zeilen: VergleichZeile[];
  offerten: OfferScoreErgebnis[];
  score_empfehlung_offer_id: string;
}

// -- Kennzahl row keys ---------------------------------------------------------

export const VERGLEICH_KENNZAHL_KEYS = {
  PRAEMIE_JAHR: 'praemie_jahr',
  VERSICHERUNGSSUMME: 'versicherungssumme',
  SELBSTBEHALT: 'selbstbehalt',
  VERTRAGSDAUER: 'vertragsdauer',
  AUSSCHLUESSE: 'ausschluesse',
} as const;

// -- Internal helpers (pure) ---------------------------------------------------

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** CHF with apostrophe grouping, no decimals (for ist_text). */
function chfText(n: number | null): string {
  if (n === null) return '-';
  const rounded = Math.round(n);
  const sign = rounded < 0 ? '-' : '';
  const digits = Math.abs(rounded).toString();
  let grouped = '';
  for (let i = 0; i < digits.length; i += 1) {
    if (i > 0 && (digits.length - i) % 3 === 0) grouped += "'";
    grouped += digits[i];
  }
  return `${sign}CHF ${grouped}`;
}

function cellScore(ampel: VergleichAmpel): number | null {
  return OFFER_SCORE_AMPEL_PUNKTE[ampel];
}

function minNonNull(values: Array<number | null>): number | null {
  let best: number | null = null;
  for (const v of values) {
    if (v === null) continue;
    if (best === null || v < best) best = v;
  }
  return best;
}

// -- The Soll-Ist matrix + Ampel (D2) ------------------------------------------

function mindestanforderungOf(regel: VergleichSollRegel): boolean {
  return (
    regel.pflicht_status === 'pflicht' ||
    regel.pflicht_status === 'bedingt_pflicht' ||
    regel.status === 'bestaetigt'
  );
}

function praemieCell(wert: number | null, pmin: number | null): VergleichZelle {
  if (wert === null) {
    return { ist_text: '-', wert: null, status: null, gap_prozent: null, ampel: 'grau', hinweis: 'Praemie fehlt' };
  }
  let ampel: VergleichAmpel = 'gruen';
  let gap: number | null = null;
  if (pmin !== null && pmin > 0) {
    if (wert <= pmin) {
      ampel = 'gruen';
    } else {
      const ueber = ((wert - pmin) / pmin) * 100;
      gap = round1(ueber);
      ampel = ueber <= OFFER_PREIS_GELB_BIS_PROZENT ? 'gelb' : 'rot';
    }
  }
  return {
    ist_text: chfText(wert),
    wert,
    status: null,
    gap_prozent: gap,
    ampel,
    hinweis: gap !== null ? `${gap} % ueber der guenstigsten Offerte` : null,
  };
}

function versicherungssummeCell(wert: number | null, sollZiel: number | null): VergleichZelle {
  if (wert === null) {
    return { ist_text: '-', wert: null, status: null, gap_prozent: null, ampel: 'grau', hinweis: null };
  }
  if (sollZiel === null || sollZiel <= 0) {
    return { ist_text: chfText(wert), wert, status: null, gap_prozent: null, ampel: 'neutral', hinweis: null };
  }
  if (wert >= sollZiel) {
    return { ist_text: chfText(wert), wert, status: null, gap_prozent: null, ampel: 'gruen', hinweis: null };
  }
  const gap = round1(((sollZiel - wert) / sollZiel) * 100);
  const ampel: VergleichAmpel = gap < OFFER_AMPEL_GAP_ROT_AB_PROZENT ? 'gelb' : 'rot';
  return { ist_text: chfText(wert), wert, status: null, gap_prozent: gap, ampel, hinweis: null };
}

function selbstbehaltCell(
  wert: number | null,
  sollSb: number | null,
  sbMin: number | null,
): VergleichZelle {
  if (wert === null) {
    return { ist_text: '-', wert: null, status: null, gap_prozent: null, ampel: 'grau', hinweis: null };
  }
  if (sollSb !== null) {
    let ampel: VergleichAmpel;
    if (wert <= sollSb) ampel = 'gruen';
    else if (wert <= sollSb * OFFER_SB_GELB_BIS_FAKTOR) ampel = 'gelb';
    else ampel = 'rot';
    const hinweis = ampel === 'rot' ? `Selbstbehalt ${chfText(wert)} ueber dem Sollwert von ${chfText(sollSb)}` : null;
    return { ist_text: chfText(wert), wert, status: null, gap_prozent: null, ampel, hinweis };
  }
  // No Soll SB: relative (lowest gruen, others gelb).
  const ampel: VergleichAmpel = sbMin !== null && wert <= sbMin ? 'gruen' : 'gelb';
  return { ist_text: chfText(wert), wert, status: null, gap_prozent: null, ampel, hinweis: null };
}

function ausschluesseCell(liste: string[]): VergleichZelle {
  const n = liste.length;
  let ampel: VergleichAmpel = 'gruen';
  if (n >= 3) ampel = 'rot';
  else if (n >= 1) ampel = 'gelb';
  return {
    ist_text: n === 0 ? 'keine' : liste.join(', '),
    wert: n,
    status: null,
    gap_prozent: null,
    ampel,
    hinweis: null,
  };
}

function sollRegelCell(
  deckung: OfferNormalizedV1['deckungen'][number] | undefined,
  sollZiel: number | null,
): VergleichZelle {
  if (deckung === undefined || deckung.status === 'unbekannt') {
    return {
      ist_text: 'nicht erkannt',
      wert: null,
      status: deckung ? deckung.status : null,
      gap_prozent: null,
      ampel: 'grau',
      hinweis: null,
    };
  }
  if (deckung.status === 'ausgeschlossen') {
    return {
      ist_text: 'ausgeschlossen',
      wert: null,
      status: 'ausgeschlossen',
      gap_prozent: null,
      ampel: 'rot',
      hinweis: null,
    };
  }
  // eingeschlossen
  if (sollZiel === null || sollZiel <= 0) {
    return {
      ist_text: deckung.deckungssumme !== null ? chfText(deckung.deckungssumme) : 'eingeschlossen',
      wert: deckung.deckungssumme,
      status: 'eingeschlossen',
      gap_prozent: null,
      ampel: 'gruen',
      hinweis: null,
    };
  }
  if (deckung.deckungssumme === null) {
    return {
      ist_text: 'eingeschlossen',
      wert: null,
      status: 'eingeschlossen',
      gap_prozent: null,
      ampel: 'gelb',
      hinweis: 'Deckungssumme nicht erkannt',
    };
  }
  if (deckung.deckungssumme >= sollZiel) {
    return {
      ist_text: chfText(deckung.deckungssumme),
      wert: deckung.deckungssumme,
      status: 'eingeschlossen',
      gap_prozent: null,
      ampel: 'gruen',
      hinweis: null,
    };
  }
  const gap = round1(((sollZiel - deckung.deckungssumme) / sollZiel) * 100);
  const ampel: VergleichAmpel = gap < OFFER_AMPEL_GAP_ROT_AB_PROZENT ? 'gelb' : 'rot';
  return {
    ist_text: chfText(deckung.deckungssumme),
    wert: deckung.deckungssumme,
    status: 'eingeschlossen',
    gap_prozent: gap,
    ampel,
    hinweis: null,
  };
}

function zusatzdeckungCell(
  deckung: OfferNormalizedV1['deckungen'][number] | undefined,
): VergleichZelle {
  if (deckung === undefined) {
    return { ist_text: 'nicht erwaehnt', wert: null, status: null, gap_prozent: null, ampel: 'grau', hinweis: null };
  }
  if (deckung.status === 'eingeschlossen') {
    return {
      ist_text: deckung.deckungssumme !== null ? chfText(deckung.deckungssumme) : 'eingeschlossen',
      wert: deckung.deckungssumme,
      status: 'eingeschlossen',
      gap_prozent: null,
      ampel: 'gruen',
      hinweis: null,
    };
  }
  if (deckung.status === 'ausgeschlossen') {
    return { ist_text: 'ausgeschlossen', wert: null, status: 'ausgeschlossen', gap_prozent: null, ampel: 'rot', hinweis: null };
  }
  return { ist_text: 'nicht erkannt', wert: null, status: 'unbekannt', gap_prozent: null, ampel: 'grau', hinweis: null };
}

// -- The offer-level Ampel (D2) ------------------------------------------------

export function ampelFuerOfferte(
  cells: Array<{ zeile: VergleichZeile; zelle: VergleichZelle }>,
  e4: boolean,
): Exclude<VergleichAmpel, 'neutral'> {
  if (e4) return 'rot';
  let anyRotOderGelb = false;
  let anySollGrau = false;
  for (const { zeile, zelle } of cells) {
    if (zelle.ampel === 'rot' || zelle.ampel === 'gelb') anyRotOderGelb = true;
    if (zelle.ampel === 'grau' && zeile.art === 'soll_regel') anySollGrau = true;
  }
  if (anyRotOderGelb) return 'gelb';
  if (anySollGrau) return 'grau';
  return 'gruen';
}

// -- Sub-scores ----------------------------------------------------------------

function brokerSubscore(value: number | null): VergleichSubscore {
  if (value !== null) return { wert: value, quelle: 'broker', hinweis: null };
  return { wert: OFFER_SCORE_NEUTRAL, quelle: 'default', hinweis: 'nicht bewertet - vom Broker zu setzen' };
}

function deckungSubscore(
  zeilen: VergleichZeile[],
  offerId: string,
  sollGewichtByKey: Record<string, number>,
): VergleichSubscore {
  let sumW = 0;
  let sumWS = 0;
  for (const zeile of zeilen) {
    if (zeile.art !== 'soll_regel' && zeile.art !== 'zusatzdeckung') continue;
    const zelle = zeile.zellen[offerId];
    if (!zelle) continue;
    const s = cellScore(zelle.ampel);
    if (s === null) continue;
    const w = zeile.art === 'zusatzdeckung' ? OFFER_SCORE_REGEL_GEWICHT.zusatz : (sollGewichtByKey[zeile.key] ?? 1);
    sumW += w;
    sumWS += w * s;
  }
  if (sumW === 0) return { wert: OFFER_SCORE_NEUTRAL, quelle: 'default', hinweis: null };
  return { wert: round1(sumWS / sumW), quelle: 'berechnet', hinweis: null };
}

function preisSubscore(wert: number | null, pmin: number | null): VergleichSubscore {
  if (wert === null) return { wert: 0, quelle: 'berechnet', hinweis: 'Praemie fehlt' };
  if (pmin === null || pmin <= 0) return { wert: 100, quelle: 'berechnet', hinweis: null };
  return { wert: round1(Math.max(0, 100 - (100 * (wert - pmin)) / pmin)), quelle: 'berechnet', hinweis: null };
}

function selbstbehaltSubscore(
  sbZelle: VergleichZelle,
  wert: number | null,
  sbMin: number | null,
  hasSollSb: boolean,
): VergleichSubscore {
  if (wert === null) return { wert: OFFER_SCORE_NEUTRAL, quelle: 'berechnet', hinweis: null };
  if (hasSollSb) {
    const s = cellScore(sbZelle.ampel);
    return { wert: s ?? OFFER_SCORE_NEUTRAL, quelle: 'berechnet', hinweis: null };
  }
  if (sbMin === null || sbMin <= 0 || wert <= sbMin) return { wert: 100, quelle: 'berechnet', hinweis: null };
  return { wert: round1(Math.max(0, 100 - (100 * (wert - sbMin)) / sbMin)), quelle: 'berechnet', hinweis: null };
}

function ausschluesseSubscore(n: number): VergleichSubscore {
  return { wert: Math.max(0, 100 - OFFER_SCORE_AUSSCHLUSS_ABZUG * n), quelle: 'berechnet', hinweis: null };
}

function versichererqualitaetSubscore(
  eignung: VergleichInputOffer['eignung'],
  aktive: number | null,
): VergleichSubscore {
  let base: number = OFFER_SCORE_EIGNUNG_BASIS.unbekannt;
  if (eignung === 'geeignet') base = OFFER_SCORE_EIGNUNG_BASIS.geeignet;
  else if (eignung === 'ausgeschlossen') base = OFFER_SCORE_EIGNUNG_BASIS.ausgeschlossen;
  else if (eignung === 'unbekannt' || eignung === null) base = OFFER_SCORE_EIGNUNG_BASIS.unbekannt;
  const bonus = aktive !== null ? Math.min(OFFER_SCORE_EIGNUNG_BONUS_MAX, 2 * aktive) : 0;
  return { wert: Math.min(100, base + bonus), quelle: 'berechnet', hinweis: null };
}

function weightedTotal(subscores: VergleichSubscores, gewichte: OfferScoreGewichte): number {
  let total = 0;
  for (const dim of OFFER_SCORE_DIMENSION_VALUES) {
    total += gewichte[dim] * subscores[dim].wert;
  }
  return round1(total / 100);
}

// -- berechneOffertVergleich (D2) ----------------------------------------------

export function berechneOffertVergleich(input: VergleichInput): VergleichErgebnis {
  const { soll, gewichte } = input;
  // A sparse normalised offer may omit the deckungen / ausschluesse arrays; default them so a data-less
  // offer still scores (grau / 0) instead of throwing a TypeError (500). [review m5]
  const offerten = input.offerten.map((o) => ({
    ...o,
    normalized: {
      ...o.normalized,
      deckungen: o.normalized.deckungen ?? [],
      ausschluesse: o.normalized.ausschluesse ?? [],
    },
  }));
  const offerIds = offerten.map((o) => o.offer_document_id);

  // -- Kennzahl aggregate contexts ------------------------------------------
  const premiums = offerten.map((o) => o.normalized.praemie.jahr);
  const pmin = minNonNull(premiums);
  const sbValues = offerten.map((o) => o.normalized.selbstbehalt);
  const sbMin = minNonNull(sbValues);

  // Primary Soll target = first Soll row with a non-null effective_target_value.
  const primarySoll = soll.find((s) => s.effective_target_value !== null) ?? null;
  // Soll SB = first non-null selbstbehalt_override (answer order).
  const sollSbRegel = soll.find((s) => s.selbstbehalt_override !== null) ?? null;
  const sollSb = sollSbRegel ? sollSbRegel.selbstbehalt_override : null;

  const zeilen: VergleichZeile[] = [];

  const addZeile = (
    key: string,
    art: VergleichZeileArt,
    label: string,
    soll_text: string,
    mindestanforderung: boolean,
    rule_key: string | null,
    cellFor: (offer: VergleichInputOffer) => VergleichZelle,
  ): void => {
    const zellen: Record<string, VergleichZelle> = {};
    for (const offer of offerten) zellen[offer.offer_document_id] = cellFor(offer);
    zeilen.push({ key, art, label, soll_text, mindestanforderung, rule_key, zellen });
  };

  // -- (K) Kennzahlen --------------------------------------------------------
  addZeile(
    VERGLEICH_KENNZAHL_KEYS.PRAEMIE_JAHR,
    'kennzahl',
    'Jahrespraemie',
    'moeglichst tief',
    false,
    null,
    (o) => praemieCell(o.normalized.praemie.jahr, pmin),
  );
  addZeile(
    VERGLEICH_KENNZAHL_KEYS.VERSICHERUNGSSUMME,
    'kennzahl',
    'Versicherungssumme',
    primarySoll && primarySoll.effective_target_value !== null ? chfText(primarySoll.effective_target_value) : '-',
    false,
    null,
    (o) => versicherungssummeCell(o.normalized.versicherungssumme, primarySoll ? primarySoll.effective_target_value : null),
  );
  addZeile(
    VERGLEICH_KENNZAHL_KEYS.SELBSTBEHALT,
    'kennzahl',
    'Selbstbehalt',
    sollSb !== null ? `<= ${chfText(sollSb)}` : '-',
    false,
    null,
    (o) => selbstbehaltCell(o.normalized.selbstbehalt, sollSb, sbMin),
  );
  addZeile(
    VERGLEICH_KENNZAHL_KEYS.VERTRAGSDAUER,
    'kennzahl',
    'Vertragsdauer',
    '-',
    false,
    null,
    (o) => {
      const m = o.normalized.vertragsdauer_monate;
      return {
        ist_text: m !== null ? `${m} Monate` : '-',
        wert: m,
        status: null,
        gap_prozent: null,
        ampel: 'neutral',
        hinweis: null,
      };
    },
  );
  addZeile(
    VERGLEICH_KENNZAHL_KEYS.AUSSCHLUESSE,
    'kennzahl',
    'Ausschluesse',
    '-',
    false,
    null,
    (o) => ausschluesseCell(o.normalized.ausschluesse),
  );

  // -- (S) Soll rules (answer order) ----------------------------------------
  const sollGewichtByKey: Record<string, number> = {};
  for (const regel of soll) {
    const key = `soll:${regel.rule_key}`;
    sollGewichtByKey[key] = OFFER_SCORE_REGEL_GEWICHT[regel.pflicht_status] ?? 1;
    const mind = mindestanforderungOf(regel);
    const sollTextParts: string[] = [];
    if (regel.effective_target_value !== null) sollTextParts.push(chfText(regel.effective_target_value));
    if (regel.status === 'bestaetigt') sollTextParts.push('bestaetigt');
    addZeile(
      key,
      'soll_regel',
      regel.label,
      sollTextParts.length > 0 ? sollTextParts.join(', ') : 'eingeschlossen',
      mind,
      regel.rule_key,
      (o) => {
        const d = o.normalized.deckungen.find((x) => x.rule_key === regel.rule_key);
        return sollRegelCell(d, regel.effective_target_value);
      },
    );
  }

  // -- (Z) Zusatzdeckungen (first-seen order, deduped by normalizeLabel) ----
  const seen = new Map<string, { label: string }>();
  for (const offer of offerten) {
    for (const d of offer.normalized.deckungen) {
      if (d.rule_key !== null) continue;
      const norm = normLabel(d.label);
      if (!seen.has(norm)) seen.set(norm, { label: d.label });
    }
  }
  for (const [norm, { label }] of seen) {
    addZeile(
      `zusatz:${norm}`,
      'zusatzdeckung',
      label,
      '-',
      false,
      null,
      (o) => {
        const d = o.normalized.deckungen.find((x) => x.rule_key === null && normLabel(x.label) === norm);
        return zusatzdeckungCell(d);
      },
    );
  }

  // -- Sub-scores + totals + E4 per offer -----------------------------------
  const sbZeile = zeilen.find((z) => z.key === VERGLEICH_KENNZAHL_KEYS.SELBSTBEHALT)!;
  const ergebnisse: OfferScoreErgebnis[] = offerten.map((offer) => {
    const id = offer.offer_document_id;
    const subscores: VergleichSubscores = {
      deckung: deckungSubscore(zeilen, id, sollGewichtByKey),
      preis: preisSubscore(offer.normalized.praemie.jahr, pmin),
      selbstbehalt: selbstbehaltSubscore(sbZeile.zellen[id]!, offer.normalized.selbstbehalt, sbMin, sollSb !== null),
      ausschluesse: ausschluesseSubscore(offer.normalized.ausschluesse.length),
      versichererqualitaet: versichererqualitaetSubscore(offer.eignung, offer.aktive_policen),
      service: brokerSubscore(offer.broker.service_subscore),
      individuell: brokerSubscore(offer.broker.individuell_subscore),
    };
    const scoreVor = weightedTotal(subscores, gewichte);

    // E4: any (S) mindestanforderung row with a rot cell for this offer.
    const e4RuleKeys: string[] = [];
    for (const zeile of zeilen) {
      if (zeile.art !== 'soll_regel' || !zeile.mindestanforderung) continue;
      const zelle = zeile.zellen[id];
      if (zelle && zelle.ampel === 'rot' && zeile.rule_key) e4RuleKeys.push(zeile.rule_key);
    }
    const e4Flag = e4RuleKeys.length > 0;
    const scoreTotal = e4Flag ? Math.max(0, round1(scoreVor - OFFER_SCORE_E4_ABWERTUNG_PUNKTE)) : scoreVor;

    const cells = zeilen.map((zeile) => ({ zeile, zelle: zeile.zellen[id]! }));
    const ampel = ampelFuerOfferte(cells, e4Flag);

    const soll_ist = zeilen.map((zeile) => ({ zeile_key: zeile.key, ...zeile.zellen[id]! }));

    return {
      offer_document_id: id,
      rang: 0,
      score_total: scoreTotal,
      score_vor_abwertung: scoreVor,
      subscores,
      ampel,
      e4_flag: e4Flag,
      e4_rule_keys: e4RuleKeys,
      kennzahlen: {
        praemie_jahr: offer.normalized.praemie.jahr,
        versicherungssumme: offer.normalized.versicherungssumme,
        selbstbehalt: offer.normalized.selbstbehalt,
        vertragsdauer_monate: offer.normalized.vertragsdauer_monate,
        waehrung: offer.normalized.waehrung,
      },
      ausschluesse: [...offer.normalized.ausschluesse],
      soll_ist,
    };
  });

  const carrierNameById = new Map(offerten.map((o) => [o.offer_document_id, o.carrier_name]));
  const ranked = rangiere(ergebnisse, (id) => carrierNameById.get(id) ?? '');
  const scoreEmpfehlung = ranked.find((e) => e.rang === 1)?.offer_document_id ?? offerIds[0]!;

  return { zeilen, offerten: ranked, score_empfehlung_offer_id: scoreEmpfehlung };
}

// Local label normaliser (mirrors offer.ts normalizeLabel without the import cycle risk).
function normLabel(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/é/g, 'e')
    .replace(/è/g, 'e')
    .replace(/ê/g, 'e')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '');
}

/** Re-score an offer from its persisted sub-scores with new weights / broker values (D6 PATCH). */
export function berechneSubscoresNeu(
  offer: OfferScoreErgebnis,
  _zeilen: VergleichZeile[],
  gewichte: OfferScoreGewichte,
  _alle: OfferScoreErgebnis[],
  broker: VergleichInputOffer['broker'],
): OfferScoreErgebnis {
  const subscores: VergleichSubscores = {
    ...offer.subscores,
    service: brokerSubscore(broker.service_subscore),
    individuell: brokerSubscore(broker.individuell_subscore),
  };
  const scoreVor = weightedTotal(subscores, gewichte);
  const scoreTotal = offer.e4_flag
    ? Math.max(0, round1(scoreVor - OFFER_SCORE_E4_ABWERTUNG_PUNKTE))
    : scoreVor;
  return { ...offer, subscores, score_vor_abwertung: scoreVor, score_total: scoreTotal };
}

/** The D2 ORDER BY: score_total DESC, e4 ASC, premium ASC NULLS LAST, carrier name ASC. */
export function rangiere(
  offerten: OfferScoreErgebnis[],
  carrierNameOf: (id: string) => string,
): OfferScoreErgebnis[] {
  const sorted = [...offerten].sort((a, b) => {
    if (b.score_total !== a.score_total) return b.score_total - a.score_total;
    if (a.e4_flag !== b.e4_flag) return a.e4_flag ? 1 : -1;
    const pa = a.kennzahlen.praemie_jahr;
    const pb = b.kennzahlen.praemie_jahr;
    if (pa !== pb) {
      if (pa === null) return 1;
      if (pb === null) return -1;
      return pa - pb;
    }
    return carrierNameOf(a.offer_document_id).localeCompare(carrierNameOf(b.offer_document_id));
  });
  return sorted.map((e, i) => ({ ...e, rang: i + 1 }));
}

// -- Fingerprint + staleness ---------------------------------------------------

/** sha256 hex over `rule_key|effective|sb|status` joined by newline (answer order). */
export function sollAntwortenFingerprint(soll: VergleichSollRegel[]): string {
  const lines = soll.map(
    (s) =>
      `${s.rule_key}|${s.effective_target_value ?? ''}|${s.selbstbehalt_override ?? ''}|${s.status}`,
  );
  return sha256Hex(lines.join('\n'));
}

export function istVeraltet(
  report: { input_offer_ids: string[]; input_answers_fingerprint: string },
  aktuelleOfferIds: string[],
  fingerprint: string,
): VeraltetGrund | null {
  const inputSet = new Set(report.input_offer_ids);
  const aktuellSet = new Set(aktuelleOfferIds);
  for (const id of aktuelleOfferIds) {
    if (!inputSet.has(id)) return VERALTET_GRUND.NEUE_OFFERTVERSION;
  }
  for (const id of report.input_offer_ids) {
    if (!aktuellSet.has(id)) return VERALTET_GRUND.OFFER_ENTFERNT;
  }
  if (fingerprint !== report.input_answers_fingerprint) return VERALTET_GRUND.SOLL_GEAENDERT;
  return null;
}

export function empfehlungAbweichend(r: {
  empfehlung_offer_id: string | null;
  score_empfehlung_offer_id: string;
}): boolean {
  return r.empfehlung_offer_id !== null && r.empfehlung_offer_id !== r.score_empfehlung_offer_id;
}

// -- Texts (ASCII hyphens only) ------------------------------------------------

export const VERGLEICH_KEIN_BERICHT_HINWEIS =
  'Noch kein Offertvergleich - sobald mindestens zwei Offerten normalisiert sind, kann der KI-Offertvergleich ausgefuehrt werden.';
export const VERGLEICH_ZU_WENIG_HINWEIS =
  'Mindestens zwei normalisierte Offerten sind fuer den Vergleich erforderlich.';
export const VERGLEICH_VERALTET_HINWEIS =
  'Der Vergleich ist nicht mehr aktuell - bitte erneut ausfuehren.';
export const VERGLEICH_KI_HINWEIS =
  'Die KI-Bewertung ist ein Vorschlag und ersetzt die fachliche Pruefung durch den Broker nicht (FIDLEG). Vor der Zustellung an den Kunden muss die Empfehlung freigegeben werden.';
export const VERGLEICH_FREIGABE_HINWEIS =
  'Mit der Freigabe wird die Empfehlung verbindlich dokumentiert und fuer die Kundenfreigabe (Schritt 13) bereitgestellt.';
export const VERGLEICH_E4_LABEL = 'Mindestanforderung nicht erfuellt';
export const VERGLEICH_E4_HINWEIS =
  'Diese Offerte erfuellt mindestens eine Soll-Vorgabe aus Schritt 4 nicht. Sie bleibt im Vergleich, der Score wurde um 10 Punkte abgewertet.';
export const VERGLEICH_ABWEICHEND_LABEL = 'Abweichend von der Score-Empfehlung';
export const VERGLEICH_GENERIERUNG_LAEUFT_HINWEIS =
  'Die KI-Bewertung wird erstellt - die Matrix und der Score sind bereits verfuegbar.';
export const VERGLEICH_GENERIERUNG_FEHLER_HINWEIS =
  'Die KI-Bewertung konnte nicht erstellt werden. Sie koennen die Bewertung manuell erfassen oder erneut erzeugen lassen.';
export const VERGLEICH_FREIGEGEBEN_HINWEIS =
  'Freigegeben - Aenderungen sind nur ueber einen neuen Vergleichslauf moeglich.';
export const VERGLEICH_NICHT_BEWERTET_HINWEIS = 'nicht bewertet - vom Broker zu setzen';
export const VERGLEICH_GEWICHTE_SUMME_HINWEIS = 'Die Gewichte muessen zusammen 100 % ergeben.';
export const VERGLEICH_BEGRUENDUNG_HINWEIS =
  'Bitte die Empfehlung begruenden (mindestens 20 Zeichen).';

// -- Events (Ch 11 verbatim + Slice-11 additions) ------------------------------

export const COMPARISON_EVENTS = {
  RUN_STARTED: 'comparison.run_started',
  AI_REPORT_GENERATED: 'comparison.ai_report_generated',
  AI_GENERATION_FAILED: 'comparison.ai_generation_failed',
  MINDESTANFORDERUNG_VERLETZT: 'comparison.offer.mindestanforderung_verletzt',
  BROKER_EDITED: 'comparison.broker_edited',
  DOCX_EXPORTED: 'comparison.docx_exported',
  SUPERSEDED: 'comparison.superseded',
  RECOMMENDATION_ISSUED: 'placement.recommendation.issued',
} as const;
export type ComparisonEvent = (typeof COMPARISON_EVENTS)[keyof typeof COMPARISON_EVENTS];

// -- Wire types ----------------------------------------------------------------

export interface ComparisonOfferEintrag extends OfferScoreErgebnis {
  submission_id: string;
  submission_number: string;
  carrier_uid: string;
  carrier_name: string;
  logo_url: string | null;
  version_no: number;
  ki_staerken: string[] | null;
  ki_schwaechen: string[] | null;
  ki_begruendung: string | null;
  staerken: string[] | null;
  schwaechen: string[] | null;
  broker_hinweis: string | null;
  effektive_staerken: string[];
  effektive_schwaechen: string[];
  service_subscore: number | null;
  individuell_subscore: number | null;
  ist_empfehlung: boolean;
  ist_score_empfehlung: boolean;
}

export interface ComparisonReportEintrag {
  id: string;
  lauf_nr: number;
  status: ComparisonReportStatus;
  status_label: string;
  is_aktuell: boolean;
  generation_status: ComparisonGenerationStatus;
  generation_status_label: string;
  generation_seq: number;
  generator: {
    driver: 'fixture' | 'phoeniqs' | null;
    model: string | null;
    confidence: number | null;
    is_demo: boolean;
  };
  generated_at: string | null;
  gewichte: OfferScoreGewichte;
  gewichte_quelle: GewichteQuelle;
  zeilen: VergleichZeile[];
  offerten: ComparisonOfferEintrag[];
  score_empfehlung_offer_id: string;
  empfehlung_offer_id: string | null;
  empfehlung_begruendung: string | null;
  empfehlung_abweichend: boolean;
  ki_zusammenfassung: string | null;
  zusammenfassung: string | null;
  effektive_zusammenfassung: string | null;
  broker_edited: boolean;
  broker_edited_at: string | null;
  broker_edited_by_name: string | null;
  freigegeben_at: string | null;
  freigegeben_by_name: string | null;
  docx: {
    vorhanden: boolean;
    filename: string | null;
    size_bytes: number | null;
    generated_at: string | null;
    veraltet: boolean;
    datei_url: string | null;
    rendered_with: 'pizzip' | 'fixture' | null;
  };
  veraltet: boolean;
  veraltet_grund: VeraltetGrund | null;
  input_offer_ids: string[];
  created_at: string;
  updated_at: string;
}

export type VergleichSperrgrund =
  | 'placement_locked'
  | 'placement_status_invalid'
  | 'zu_wenig_offerten'
  | 'generierung_laeuft'
  | 'zustellung_offen';

/** Slice 12 (§4.9): a freigegebener report that is already with the customer blocks a new run. */
export const VERGLEICH_ZUSTELLUNG_OFFEN_HINWEIS =
  'Der freigegebene Vergleich ist beim Kunden - ein neuer Lauf ist erst nach dem Kundenentscheid moeglich.';

export interface OfferComparisonResponse {
  placement_id: string;
  placement_number: string;
  status: PlacementStatus;
  branchencode: string;
  bericht: ComparisonReportEintrag | null;
  ausfuehren_erlaubt: boolean;
  sperrgrund: VergleichSperrgrund | null;
  anzahl_normalisiert: number;
  bearbeiten_erlaubt: boolean;
  freigeben_erlaubt: boolean;
  gewichte_tenant: OfferScoreGewichte;
}

export interface OfferComparisonRunBody {
  gewichte?: OfferScoreGewichte;
}

export interface ComparisonOfferPatch {
  offer_document_id: string;
  staerken?: string[] | null;
  schwaechen?: string[] | null;
  broker_hinweis?: string | null;
  service_subscore?: number | null;
  individuell_subscore?: number | null;
}

export interface OfferComparisonPatchBody {
  zusammenfassung?: string | null;
  empfehlung_offer_id?: string;
  empfehlung_begruendung?: string | null;
  gewichte?: OfferScoreGewichte;
  offerten?: ComparisonOfferPatch[];
  expected_updated_at?: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- reserved body
export interface OfferComparisonFreigebenBody {}

export interface OfferScoreGewichteResponse {
  gewichte: OfferScoreGewichte;
  origin: 'system' | 'admin';
  customized_at: string | null;
  catalog_version: string | null;
  default: OfferScoreGewichte;
}

export interface OfferScoreGewichtePutBody {
  gewichte: OfferScoreGewichte;
}

// -- Tiny pure sha256 (there is no shared helper; used by the fingerprint) -----

function sha256Hex(message: string): string {
  // UTF-8 encode.
  const bytes: number[] = [];
  for (let i = 0; i < message.length; i += 1) {
    let c = message.charCodeAt(i);
    if (c < 0x80) {
      bytes.push(c);
    } else if (c < 0x800) {
      bytes.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    } else if (c >= 0xd800 && c <= 0xdbff) {
      const c2 = message.charCodeAt(i + 1);
      c = 0x10000 + ((c & 0x3ff) << 10) + (c2 & 0x3ff);
      i += 1;
      bytes.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 0x3f), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    } else {
      bytes.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    }
  }

  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];
  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  const l = bytes.length;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  const bitLen = l * 8;
  for (let i = 7; i >= 0; i -= 1) bytes.push((bitLen / 2 ** (i * 8)) & 0xff);

  const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));
  const w = new Array<number>(64);
  for (let off = 0; off < bytes.length; off += 64) {
    for (let i = 0; i < 16; i += 1) {
      w[i] = (bytes[off + i * 4]! << 24) | (bytes[off + i * 4 + 1]! << 16) | (bytes[off + i * 4 + 2]! << 8) | bytes[off + i * 4 + 3]!;
    }
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15]!, 7) ^ rotr(w[i - 15]!, 18) ^ (w[i - 15]! >>> 3);
      const s1 = rotr(w[i - 2]!, 17) ^ rotr(w[i - 2]!, 19) ^ (w[i - 2]! >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) | 0;
    }
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;
    for (let i = 0; i < 64; i += 1) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i]! + w[i]!) | 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + h) | 0;
  }

  const toHex = (x: number): string => (x >>> 0).toString(16).padStart(8, '0');
  return toHex(h0) + toHex(h1) + toHex(h2) + toHex(h3) + toHex(h4) + toHex(h5) + toHex(h6) + toHex(h7);
}
